//@flow
import React, {useState} from 'react';
import {makeStyles, Typography} from '@material-ui/core';
import {Add} from '@material-ui/icons';
import CustomTextField from '../../../../../../../../elements/CustomTextField/CustomTextField';
import CustomButton from '../../../../../../../../elements/CustomButton/CustomButton';
import TagItem from '../../../../../../../../elements/TagInput/TagItem';
import {colors} from '../../../../../../../../assets/styles/colors';
import {sanitizeSimilarWords} from '../../../../../utils/adapters';

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.75rem',
    padding: '1rem',
    borderRadius: '12px',
    background: colors.grey100,
    border: `1px solid ${colors.grey300}`,
  },
  helperText: {
    fontSize: '.8125rem',
    color: colors.grey700,
  },
  inputRow: {
    display: 'flex',
    gap: '.5rem',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  },
  inputField: {
    flex: '1 1 200px',
  },
  addButton: {
    minWidth: '110px',
    height: '40px',
    marginTop: '2px',
  },
  tagList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '.5rem',
  },
  emptyHint: {
    fontSize: '.8125rem',
    color: colors.grey600,
    fontStyle: 'italic',
  },
}));

type Props = {
  words: string[],
  onChange: (words: string[]) => void,
};

export default function SimilarWordsInput({words, onChange}: Props) {
  const classes = useStyles();
  const [inputValue, setInputValue] = useState('');
  const [inputError, setInputError] = useState('');

  const sanitizedWords = sanitizeSimilarWords(words);

  const handleAddWord = () => {
    const trimmed = inputValue.trim();

    if (!trimmed) {
      setInputError('Escribe una palabra antes de agregar');
      return;
    }

    const exists = sanitizedWords.some(
      word => word.toLowerCase() === trimmed.toLowerCase()
    );

    if (exists) {
      setInputError('Esta palabra ya fue agregada');
      return;
    }

    onChange([...sanitizedWords, trimmed]);
    setInputValue('');
    setInputError('');
  };

  const handleRemoveWord = (wordToRemove: string) => {
    onChange(sanitizedWords.filter(word => word !== wordToRemove));
  };

  const handleKeyDown = (event: SyntheticKeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddWord();
    }
  };

  return (
    <div className={classes.root}>
      <Typography className={classes.helperText}>
        Agrega palabras alternativas que también deben activar esta regla.
      </Typography>

      <div className={classes.inputRow}>
        <CustomTextField
          className={classes.inputField}
          placeholder="Ej: hola, hi, buenos días"
          value={inputValue}
          onChange={event => {
            setInputValue(event.target.value);
            if (inputError) setInputError('');
          }}
          onKeyDown={handleKeyDown}
          error={!!inputError}
          helperText={inputError}
        />
        <CustomButton
          type="button"
          variant="contained"
          className={classes.addButton}
          onClick={handleAddWord}
        >
          <Add fontSize="small" style={{marginRight: '.25rem'}} />
          Agregar
        </CustomButton>
      </div>

      {sanitizedWords.length > 0 ? (
        <div className={classes.tagList}>
          {sanitizedWords.map(word => (
            <TagItem
              key={word}
              label={word}
              onDelete={() => handleRemoveWord(word)}
            />
          ))}
        </div>
      ) : (
        <Typography className={classes.emptyHint}>
          Aún no has agregado palabras similares.
        </Typography>
      )}
    </div>
  );
}
